/**
 * depositPick — an unassigned deposit, as one row of a picker.
 *
 * TWENTY-SIX DEPOSITS ARE SITTING IN THE POOL RIGHT NOW, median age fifty-one days, and roughly a
 * quarter of all deposits are paid before the contract they belong to exists. So the normal move
 * on the deposit canvas is not "record a payment" — it is "find the payment that already came in",
 * and the thing that makes an old payment recognisable is HOW OLD IT IS. The age is therefore on
 * every row, in words, beside the amount.
 *
 * PURE, AND REACT-FREE — vitest runs node-env over `.ts` only. `differenceInCalendarDays` takes
 * both ends, so "today" is an argument here rather than a hidden clock: a row that says
 * "51 days in the pool" is a claim a test can hold to.
 */

import { differenceInCalendarDays, format } from 'date-fns';

import { businessDay, formatBusinessDate, formatCurrency } from '@/lib/format';

/**
 * The part of an unassigned deposit a row reads.
 *
 * DECLARED STRUCTURALLY RATHER THAN IMPORTED FROM `financials.ts`, which imports the supabase
 * client — and that client touches `localStorage` at module scope, which takes a node-env test run
 * down. `UnassignedDeposit` satisfies this field for field.
 */
export interface PickableDeposit {
    id: number;
    remarks: string | null;
    created_at: string;
    transactions: {
        amount: number;
        transaction_at: string;
        description: string | null;
    } | null;
}

/** When the money actually moved — the transaction's date, or failing that when the row appeared. */
export function depositDate(deposit: PickableDeposit): string {
    return deposit.transactions?.transaction_at ?? deposit.created_at;
}

/**
 * `51 days in the pool` — the number that makes an old deposit look old.
 *
 * A future-dated transaction (which the bank feed does produce) reads as `today` rather than as a
 * negative age: the row's job is recognisability, not arithmetic.
 *
 * COUNTED IN THE CALENDAR THE ROW IS DATED IN. `depositDateText` prints the Ghana day and
 * `depositMeta` joins the two into one string, so counting off host-zone Dates put a contradiction
 * inside a single line: `Jun 13, 2026 · 52 days in the pool` where the business calendar says 51.
 * Both ends go through `businessDay` now, and the count still comes off the injected `now` rather
 * than `businessToday()` so a caller (and every test) keeps its own clock.
 */
export function depositAgeText(dated: string | null | undefined, now: Date = new Date()): string {
    if (!dated) return '';
    const day = businessDay(dated);
    const today = businessDay(now);
    if (!day || !today) return '';
    const days = differenceInCalendarDays(today, day);
    if (days <= 0) return 'today';
    if (days === 1) return '1 day in the pool';
    return `${days} days in the pool`;
}

/** `GH₵5,000.00` — the amount, which is what is being recognised. */
export function depositLabel(deposit: PickableDeposit): string {
    return formatCurrency(deposit.transactions?.amount ?? 0);
}

/** `Jun 12, 2026` — the day the money moved, as the row prints it. */
export function depositDateText(deposit: PickableDeposit): string {
    const dated = depositDate(deposit);
    return dated ? formatBusinessDate(dated) : '';
}

/**
 * `Kofi paid by MoMo` — whoever it was recorded against, or failing that the bank's own text.
 *
 * THE ONE UNBOUNDED STRING ON THE ROW. Remarks are free text and routinely carry a name and a
 * phone number, which is why this is the cell that gives way rather than the amount beside it.
 */
export function depositRemarksText(deposit: PickableDeposit): string {
    return deposit.remarks?.trim() || deposit.transactions?.description?.trim() || '';
}

/**
 * `Jun 12, 2026 · 51 days in the pool · Kofi paid by MoMo`.
 *
 * FOUR CELLS ON THE ROW, ONE STRING FOR THE TOOLTIP. The picker draws the parts separately so the
 * amount keeps a column of its own; this composes the same parts back into the line a pointer
 * anywhere on the row gets, so the two can never say different things.
 */
export function depositMeta(deposit: PickableDeposit, now: Date = new Date()): string {
    return [
        depositDateText(deposit),
        depositAgeText(depositDate(deposit), now),
        depositRemarksText(deposit),
    ]
        .filter(Boolean)
        .join(' · ');
}

/** Amount, date, remarks and the bank's own text — everything the row answers to. */
export function depositSearchText(deposit: PickableDeposit): string {
    // Comma-less by design so "Jun 13 2026" typed into the box matches; see transactionSearchText.
    const day = businessDay(depositDate(deposit));
    return [
        String(deposit.transactions?.amount ?? ''),
        day ? format(day, 'MMM d yyyy') : '',
        deposit.remarks ?? '',
        deposit.transactions?.description ?? '',
    ]
        .filter(Boolean)
        .join(' ');
}
